import React, { useMemo, useState } from 'react';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';

// Ordered so the chips read in a natural "card-type shelf" sweep:
// creatures → planeswalkers → the spells → permanents → lands.
const TYPE_ORDER = [
    'creature',
    'planeswalker',
    'instant',
    'sorcery',
    'artifact',
    'enchantment',
    'land',
];

function primaryType(staple) {
    for (const t of TYPE_ORDER) {
        if ((staple?.baseTypes || []).includes(t)) return t;
    }
    return (staple?.baseTypes || [])[0] || 'other';
}

export function StaplesPane({ metaBlob }) {
    const [search, setSearch] = useState('');
    const [activeType, setActiveType] = useState('all');
    const [activeCategory, setActiveCategory] = useState('all');
    const openCardInfo = useOpenCardInfo();

    const allStaples = useMemo(() => {
        if (!metaBlob?.cards) return [];
        const uniq = new Set();
        const out = [];
        for (const id of metaBlob.cards) {
            if (uniq.has(id)) continue;
            const meta = metaBlob.staplesMetadata?.[id];
            if (!meta) continue;
            uniq.add(id);
            out.push({ ...meta, primaryType: primaryType(meta) });
        }
        // Sort by meta share descending — same order the hero stats imply.
        out.sort((a, b) => Number(b.metaShare || 0) - Number(a.metaShare || 0));
        return out;
    }, [metaBlob]);

    const typeCounts = useMemo(() => {
        const counts = { all: allStaples.length };
        for (const t of TYPE_ORDER) counts[t] = 0;
        for (const s of allStaples) counts[s.primaryType] = (counts[s.primaryType] || 0) + 1;
        return counts;
    }, [allStaples]);

    // Roll up every category that appears on any staple. Counts are
    // type-aware so switching the type chip narrows the category list
    // sensibly instead of showing category chips with zero hits.
    const categoryCounts = useMemo(() => {
        const counts = {};
        for (const s of allStaples) {
            if (activeType !== 'all' && s.primaryType !== activeType) continue;
            for (const cat of s.categories || []) {
                counts[cat] = (counts[cat] || 0) + 1;
            }
        }
        return counts;
    }, [allStaples, activeType]);

    const sortedCategories = useMemo(() => {
        return Object.entries(categoryCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([key, count]) => ({ key, count }));
    }, [categoryCounts]);

    const filtered = useMemo(() => {
        const tok = search.trim().toLowerCase();
        return allStaples.filter((s) => {
            if (activeType !== 'all' && s.primaryType !== activeType) return false;
            if (activeCategory !== 'all' && !(s.categories || []).includes(activeCategory)) return false;
            if (!tok) return true;
            return (s.names || []).some((n) => n.toLowerCase().includes(tok));
        });
    }, [allStaples, activeType, activeCategory, search]);

    if (!metaBlob?.cards) {
        return (
            <div className="metaPaneEmpty">
                <span>Staples list is still loading — check back in a moment.</span>
            </div>
        );
    }

    return (
        <div className="metaStaplesPane">
            <div className="metaFilterRow">
                <input
                    className="metaFilterInput"
                    type="text"
                    placeholder="Filter staples…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    spellCheck={false}
                />
                <span className="metaFilterHint">
                    Meta % = share of parsed cEDH decks that run the card. Sorted hottest first.
                </span>
            </div>

            <div className="metaTypeChipRow">
                <TypeChip
                    label="All"
                    count={typeCounts.all}
                    active={activeType === 'all'}
                    onClick={() => {
                        setActiveType('all');
                        // Categories can shift when the type changes — if the
                        // active category no longer has members, reset to all.
                        if (activeCategory !== 'all' && !categoryCounts[activeCategory]) {
                            setActiveCategory('all');
                        }
                    }}
                />
                {TYPE_ORDER.map((t) => {
                    const count = typeCounts[t] || 0;
                    if (!count) return null;
                    return (
                        <TypeChip
                            key={t}
                            label={t[0].toUpperCase() + t.slice(1)}
                            count={count}
                            active={activeType === t}
                            onClick={() => {
                                setActiveType(t);
                                if (activeCategory !== 'all' && !categoryCounts[activeCategory]) {
                                    setActiveCategory('all');
                                }
                            }}
                        />
                    );
                })}
            </div>

            {sortedCategories.length > 0 && (
                <div className="metaCategoryChipRow">
                    <span className="metaCategoryChipRowLabel">CATEGORY</span>
                    <button
                        type="button"
                        className={`metaCategoryChip${activeCategory === 'all' ? ' metaCategoryChip--active' : ''}`}
                        onClick={() => setActiveCategory('all')}
                    >
                        <span className="metaCategoryChipLabel">All</span>
                    </button>
                    {sortedCategories.map((c) => (
                        <button
                            key={c.key}
                            type="button"
                            className={`metaCategoryChip${activeCategory === c.key ? ' metaCategoryChip--active' : ''}`}
                            onClick={() =>
                                setActiveCategory(activeCategory === c.key ? 'all' : c.key)
                            }
                        >
                            <span className="metaCategoryChipLabel">
                                {getPrettyLabel(c.key) || c.key}
                            </span>
                            <span className="metaCategoryChipCount">{c.count}</span>
                        </button>
                    ))}
                </div>
            )}

            {filtered.length === 0 ? (
                <div className="metaPaneEmpty">
                    Nothing matches{search ? ` "${search}"` : ''} in this slice.
                </div>
            ) : (
                <div className="metaStapleGrid">
                    {filtered.map((staple, idx) => (
                        <StapleTile
                            key={staple.id}
                            entry={staple}
                            rank={idx + 1}
                            onOpen={() =>
                                openCardInfo({
                                    id: staple.id,
                                    cardId: staple.id,
                                    name: (staple.names || [])[0],
                                    imageUri: (staple.imageUris || [])[0],
                                    types: (staple.baseTypes || []).join(' '),
                                })
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function TypeChip({ label, count, active, onClick }) {
    return (
        <button
            type="button"
            className={`metaTypeChip${active ? ' metaTypeChip--active' : ''}`}
            onClick={onClick}
        >
            <span className="metaTypeChipLabel">{label}</span>
            <span className="metaTypeChipCount">{count}</span>
        </button>
    );
}

function StapleTile({ entry, rank, onOpen }) {
    const share = Number(entry.metaShare || 0);
    const name = (entry.names || [])[0] || entry.id;
    const imageUri = (entry.imageUris || [])[0];
    const categories = entry.categories || [];

    return (
        <button type="button" className="metaStapleTile" onClick={onOpen} title={name}>
            <span className="metaStapleRank">#{rank}</span>
            <div className="metaStapleArtFrame">
                {imageUri ? (
                    <img src={imageUri} alt={name} loading="lazy" className="metaStapleArt" />
                ) : (
                    <span className="metaStapleArtFallback">{name}</span>
                )}
            </div>

            <div className="metaStapleBody">
                <span className="metaStapleName">{name}</span>

                <div className="metaStapleShareRow">
                    <div className="metaStapleShareBar">
                        <div
                            className="metaStapleShareFill"
                            style={{ width: `${Math.max(2, Math.min(100, share))}%` }}
                        />
                    </div>
                    <span className="metaStapleShareValue">{share.toFixed(1)}%</span>
                </div>

                <div className="metaStapleMetaRow">
                    <span className="metaStapleType">{entry.primaryType}</span>
                    <span className="metaStapleCount">{Number(entry.count || 0).toLocaleString()} copies</span>
                </div>

                {categories.length > 0 && (
                    <div className="metaStapleCategories">
                        {categories.slice(0, 3).map((cat) => (
                            <span key={cat} className="metaStapleCategoryChip">
                                {getPrettyLabel(cat) || cat}
                            </span>
                        ))}
                        {categories.length > 3 && (
                            <span className="metaStapleCategoryMore">+{categories.length - 3}</span>
                        )}
                    </div>
                )}
            </div>
        </button>
    );
}
